import { string } from "./model";

export const SPACING: Record<string, string> = {
  none: "0px",
  xs: "4px",
  sm: "8px",
  md: "16px",
  lg: "32px",
  xl: "48px",
  "2xl": "96px",
};
/**
 * Luft indeni's and Luft udenom's steps of Tæthed's scale (Design › Afstand og bredde): `space-md` is `--re-space-md`,
 * which style-system.ts scales by Tæthed and on a phone by 0.75 more. The fallback is the step at Normal on a computer,
 * which a website off the look system gets.
 */
export const DENSITY_SPACING: Record<string, string> = {
  "space-xs": "var(--re-space-xs, 4px)",
  "space-sm": "var(--re-space-sm, 8px)",
  "space-md": "var(--re-space-md, 16px)",
  "space-lg": "var(--re-space-lg, 24px)",
  "space-xl": "var(--re-space-xl, 32px)",
  "space-2xl": "var(--re-space-2xl, 48px)",
  "space-3xl": "var(--re-space-3xl, 64px)",
};
const LENGTH = /^-?\d+(?:\.\d+)?(?:px|rem|em|vh|vw|svh|%)$/i;
const NON_NEGATIVE_LENGTH = /^\d+(?:\.\d+)?(?:px|rem|em|vh|vw|%)$/i;

export function isCssLength(value: unknown): value is string {
  return typeof value === "string" && LENGTH.test(value.trim());
}

/** One gap or distance: a spacing token or a non-negative CSS length; anything else is undefined (the CSS default). */
export function lengthOrToken(raw: unknown): string | undefined {
  const value = string(raw).trim();
  if (value in SPACING) return SPACING[value];
  return NON_NEGATIVE_LENGTH.test(value) ? value : undefined;
}

/** One gap per device, `mobile | desktop` (one part applies to both); an unreadable part is undefined. */
export function responsiveLength(raw: unknown): {
  mobile?: string;
  desktop?: string;
} {
  if (typeof raw !== "string") return {};
  const [mobile, desktop = mobile] = raw.split("|");
  return { mobile: lengthOrToken(mobile), desktop: lengthOrToken(desktop) };
}

/** 1–4 spacing tokens or non-negative CSS lengths in CSS shorthand order; anything else is undefined. */
export function lengthBox(raw: unknown): string | undefined {
  const values = string(raw).trim().split(/\s+/).map(lengthOrToken);
  return values.length <= 4 && values.every(Boolean)
    ? values.join(" ")
    : undefined;
}

/** 1–4 spacing tokens in CSS shorthand order; anything else gives `fallback`. */
export function spacingCss(raw: unknown, fallback = "0px"): string {
  const values = string(raw).trim().split(/\s+/);
  return values.length <= 4 && values.every((v) => v in SPACING)
    ? values.map((v) => SPACING[v]).join(" ")
    : fallback;
}

export interface BoxSides {
  top: string;
  right: string;
  bottom: string;
  left: string;
}

/** One side of a box: a spacing token, a Tæthed step or a CSS length; anything else counts as 0. */
export function boxSide(token: string): string {
  if (token in SPACING) return SPACING[token];
  if (Object.hasOwn(DENSITY_SPACING, token)) return DENSITY_SPACING[token];
  return isCssLength(token) ? token : "0px";
}

/** One screen's box: 1–4 spacing tokens, Tæthed steps or CSS lengths, CSS shorthand order; anything else counts as 0. */
function boxSides(part: string): BoxSides | undefined {
  const tokens = part.trim().split(/\s+/).filter(Boolean);
  if (!tokens.length || tokens.length > 4) return undefined;
  const css = tokens.map(boxSide);
  const [a, b = a, c = a, d = b] = css;
  return { top: a, right: b, bottom: c, left: d };
}

/** `mobile | desktop` boxes; a single box applies to both screens. */
export function boxCss(raw: unknown): {
  mobile?: BoxSides;
  desktop?: BoxSides;
} {
  if (typeof raw !== "string") return {};
  const parts = raw.split("|");
  const mobile = boxSides(parts[0] ?? "");
  const desktop = parts.length > 1 ? boxSides(parts[1] ?? "") : mobile;
  return { mobile, desktop };
}
